// src/agent/contribuables/Rapport.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import {
  Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Badge,
  Toast, ToastContainer, Spinner, ProgressBar, Tab, Tabs, Dropdown,
  Placeholder, Modal,
} from "react-bootstrap";
import axios from "axios";
import {
  Search, Download, ClockHistory, XCircle, FileEarmarkBarGraph,
  CurrencyDollar, PieChart, FileEarmarkPdf, Trophy, Person, People,
  ArrowUp, Phone, Envelope, Calendar, PersonPlus, GraphUp, BarChart,
  Receipt, FiletypeCsv, FiletypeXlsx, ExclamationCircle, Shop,
  FileEarmarkText, PencilSquare,
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";
import {
  getUserRoleId, getUserRoles, isSuperAdmin as checkSuperAdmin,
} from "../../utils/auth";

const ALLOWED_MARKET_SCOPE_ROLES = [1002];

const getDefaultDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => {
    const jj = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const aa = d.getFullYear();
    return `${aa}-${mm}-${jj}`;
  };
  return { startDate: fmt(firstDay), endDate: fmt(lastDay), period: "month" };
};

const extractLieuFromEntiteName = (nomEntite) => {
  if (!nomEntite) return "";
  return nomEntite
    .replace(/^Commune\s+d[eu]?\s*/i, "")
    .replace(/^COMMUNE\s+/i, "")
    .trim()
    .toUpperCase();
};

const cleanMatricule = (agent) => {
  const mat = agent.matricule_interne || agent.code_agent_interne || "";
  if (!mat) return "";
  return String(mat).replace(/^AG-?/i, "").trim();
};

// ✅ Charge une image en base64 pour l'injecter dans l'iframe d'impression
const loadImageAsBase64 = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error(`Impossible de charger : ${url}`));
    img.src = url;
  });

export default function RapportsUsagers() {
  const [activeTab, setActiveTab] = useState("apercu");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "info" });
  const [marketScope, setMarketScope] = useState("all");

  const [rapportData, setRapportData] = useState({
    stats_globaux: null,
    contribuables: { data: [], pagination: { total: 0, current_page: 1, per_page: 10, last_page: 1 } },
    collectes: { data: [], stats: {}, pagination: { total: 0, current_page: 1, per_page: 10, last_page: 1 } },
    enrollement: { global: null, parAgent: [], parJour: [], totauxAgents: null, pagination: { current_page: 1, per_page: 10, total: 0, last_page: 1 } },
    evolution: [],
    journalier: [],
  });

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [contribuablesFilters, setContribuablesFilters] = useState({ page: 1, per_page: 10, search: "" });
  const [collectesFilters, setCollectesFilters] = useState({ page: 1, per_page: 10, search: "", statut: "" });
  const [agentsFilters, setAgentsFilters] = useState({ page: 1, per_page: 10, search: "" });

  const [pvModalOpen, setPvModalOpen] = useState(false);
  const [pvData, setPvData] = useState(null);
  const [pvMeta, setPvMeta] = useState({
    service: "",
    lieu: "",
    dateValidation: new Date().toISOString().split("T")[0],
    responsableCollecte: "",
    responsableFinancier: "",
    responsableValidation: "",
    observations: "",
  });

  const [pvPreviewOpen, setPvPreviewOpen] = useState(false);
  const [allAgentsForPV, setAllAgentsForPV] = useState([]);
  const [loadingAllAgents, setLoadingAllAgents] = useState(false);
  const [loadingPV, setLoadingPV] = useState(false);

  // ✅ Logos préchargés en base64 pour l'aperçu (rendu synchrone)
  const [pvLogos, setPvLogos] = useState({ logoLeft: "", logoRight: "" });
  // ✅ Indique que les logos sont prêts (chargés en base64)
  const [logosReady, setLogosReady] = useState(false);

  const userRoleId = useMemo(() => getUserRoleId() || 0, []);
  const isSuperAdmin = useMemo(() => checkSuperAdmin(), []);
  const isAdminEntite = userRoleId === 1002;

  const canUseMarketScope = useMemo(() => {
    if (isSuperAdmin === true) return true;
    if (ALLOWED_MARKET_SCOPE_ROLES.includes(userRoleId)) return true;
    return false;
  }, [isSuperAdmin, userRoleId]);

  const defaultCurrency = sessionStorage.getItem("default_currency") || "CDF";
  const getAuthToken = () => sessionStorage.getItem("authToken") || localStorage.getItem("authToken");

  const getHeaders = () => {
    const token = getAuthToken();
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " " + defaultCurrency;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const showAlert = (message, type = "info") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert((prev) => ({ ...prev, show: false })), 4000);
  };

  // ✅ Précharge les logos une seule fois au montage pour l'aperçu modal
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadImageAsBase64("/img/logominerve.png").catch((e) => {
        console.warn("Logo gauche non chargé :", e.message);
        return "";
      }),
      loadImageAsBase64("/img/image.jpeg").catch((e) => {
        console.warn("Logo droit non chargé :", e.message);
        return "";
      }),
    ]).then(([logoLeft, logoRight]) => {
      if (cancelled) return;
      setPvLogos({ logoLeft, logoRight });
      setLogosReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  const updatePeriod = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    setDateRange({ startDate: formatDateForAPI(startDate), endDate: formatDateForAPI(endDate), period: days });
  };

  const resetDateRange = () => setDateRange(getDefaultDateRange());

  const fetchJournalierForPeriod = async () => {
    const params = {
      start_date: dateRange.startDate, end_date: dateRange.endDate,
      market_scope: canUseMarketScope ? marketScope : "all",
      contribuables_per_page: 1, collectes_per_page: 1, agents_per_page: 1,
    };
    const response = await axios.get(`${API_BASE_URL}/rapports/complet`, { headers: getHeaders(), params });
    return response.data?.journalier || [];
  };

  const formatJournalierRow = (j) => ({
    date: j.date,
    dateLabel: new Date(j.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }),
    jourSemaine: new Date(j.date).toLocaleDateString("fr-FR", { weekday: "long" }),
    attendu: parseFloat(j.attendu || 0),
    collecte: parseFloat(j.collecte || 0),
    pourcentage: parseFloat(j.pourcentage || 0),
    nombreCollectes: parseInt(j.nombre_collectes || 0, 10),
  });

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange.startDate, end_date: dateRange.endDate,
        market_scope: canUseMarketScope ? marketScope : "all",
        contribuables_page: contribuablesFilters.page, contribuables_per_page: contribuablesFilters.per_page, contribuables_search: contribuablesFilters.search,
        collectes_page: collectesFilters.page, collectes_per_page: collectesFilters.per_page, collectes_search: collectesFilters.search, collectes_statut: collectesFilters.statut,
        agents_page: agentsFilters.page, agents_per_page: agentsFilters.per_page, agents_search: agentsFilters.search,
      };
      const response = await axios.get(`${API_BASE_URL}/rapports/complet`, { headers: getHeaders(), params });
      if (response.data.success) {
        setRapportData({ ...response.data, journalier: response.data.journalier || [] });
      } else {
        showAlert(response.data.message || "Erreur lors du chargement", "danger");
      }
    } catch (error) {
      console.error("=== ERREUR COMPLÈTE ===");
      if (error.response) showAlert(`Erreur ${error.response.status}: ${error.response.data?.message || error.message}`, "danger");
      else if (error.request) showAlert("Pas de réponse du serveur.", "danger");
      else showAlert(`Erreur: ${error.message}`, "danger");
    } finally { setLoading(false); }
  }, [dateRange.startDate, dateRange.endDate, marketScope, canUseMarketScope, contribuablesFilters, collectesFilters, agentsFilters]);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  const loadAllAgentsForPV = useCallback(async () => {
    setLoadingAllAgents(true);
    try {
      const params = {
        start_date: dateRange.startDate, end_date: dateRange.endDate,
        market_scope: canUseMarketScope ? marketScope : "all",
        contribuables_per_page: 1, collectes_per_page: 1,
        agents_page: 1, agents_per_page: 1000, agents_search: "",
      };
      const response = await axios.get(`${API_BASE_URL}/rapports/complet`, { headers: getHeaders(), params });
      if (response.data.success) setAllAgentsForPV(response.data.enrollement?.parAgent || []);
    } catch (error) {
      console.error("Erreur chargement agents PV:", error);
      showAlert("Erreur lors du chargement des agents pour le PV", "danger");
    } finally { setLoadingAllAgents(false); }
  }, [dateRange.startDate, dateRange.endDate, marketScope, canUseMarketScope]);

  const generatePDFContent = () => {
    const dateGeneration = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const stats = rapportData.stats_globaux || {};
    const contribuablesList = rapportData.contribuables?.data || [];
    const agentsList = rapportData.enrollement?.parAgent || [];
    const evolution = rapportData.evolution || [];
    const scopeLabel = marketScope === "all" ? "Tous" : marketScope === "no_market" ? "Hors marché" : "De marché";
    const top5Contribuables = [...contribuablesList].sort((a, b) => b.montantInitialTotal - a.montantInitialTotal).slice(0, 5);

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Rapport complet - SyncTaxe</title>
<style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; padding: 20px; background: white; } @media print { body { padding: 10px; } }
.header { text-align: center; margin-bottom: 25px; padding-bottom: 15px; border-bottom: 3px solid #1a3c5e; } .header h1 { font-size: 22px; color: #1a1a1a; margin-bottom: 5px; } .header .subtitle { font-size: 11px; color: #666; } .header .date { font-size: 9px; color: #888; margin-top: 5px; }
.section { margin-bottom: 20px; page-break-inside: avoid; } .section-title { font-size: 14px; font-weight: 600; color: #1a3c5e; margin-bottom: 10px; padding-bottom: 4px; border-bottom: 2px solid #1a3c5e; }
.stats-row { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; } .stat-box { flex: 1; min-width: 120px; padding: 12px; background: #f5f5f5; text-align: center; border-left: 3px solid #1a3c5e; } .stat-number { font-size: 16px; font-weight: 700; color: #1a3c5e; } .stat-label { font-size: 9px; color: #666; text-transform: uppercase; }
table { width: 100%; border-collapse: collapse; margin-bottom: 10px; } th { background: #1a3c5e; color: white; padding: 8px 10px; text-align: left; font-weight: 600; font-size: 10px; } td { padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 9px; }
.text-center { text-align: center; } .text-right { text-align: right; } .text-success { color: #28a745; } .text-danger { color: #dc3545; }
.footer { margin-top: 25px; padding-top: 10px; border-top: 1px solid #ddd; text-align: center; font-size: 8px; color: #888; }
</style></head><body>
<div class="header"><h1>SYNCTAXE</h1><div class="subtitle">Système de gestion fiscale</div><div class="subtitle">Rapport complet des statistiques</div><div class="date">Généré le ${dateGeneration}</div>
${dateRange.startDate || dateRange.endDate ? `<div class="date">Période : ${dateRange.startDate || "Début"} → ${dateRange.endDate || "Aujourd'hui"}</div>` : ""}
${canUseMarketScope ? `<div class="date">Périmètre : ${scopeLabel}${isSuperAdmin ? " (Super Admin)" : ""}</div>` : ""}</div>
<div class="section"><div class="section-title">1. APERÇU GÉNÉRAL</div>
<div class="stats-row"><div class="stat-box"><div class="stat-number">${stats.totalContribuables || 0}</div><div class="stat-label">CONTRIBUABLES</div></div>
<div class="stat-box"><div class="stat-number">${stats.tauxRecouvrement?.toFixed(1) || 0}%</div><div class="stat-label">TAUX RECOUVREMENT</div></div>
<div class="stat-box"><div class="stat-number">${formatCurrency(stats.totalAttendu || 0)}</div><div class="stat-label">MONTANT TOTAL</div></div></div>
<table><thead><tr><th>Indicateur</th><th class="text-right">Valeur</th></tr></thead><tbody>
<tr><td>Total attendu</td><td class="text-right">${formatCurrency(stats.totalAttendu || 0)}</td></tr>
<tr><td>Total payé</td><td class="text-right text-success">${formatCurrency(stats.totalPaye || 0)}</td></tr>
<tr><td>Reste à payer</td><td class="text-right">${formatCurrency(stats.totalRestant || 0)}</td></tr>
</tbody></table></div>
<div class="section"><div class="section-title">2. TOP 5 CONTRIBUABLES</div>
<table><thead><tr><th>Nom</th><th class="text-center">Sousc.</th><th class="text-right">Total</th><th class="text-right">Payé</th><th class="text-center">Taux</th></tr></thead><tbody>
${top5Contribuables.map((c, idx) => `<tr><td>${idx + 1}. ${c.nom}</td><td class="text-center">${c.nombreSouscriptions}</td><td class="text-right">${formatCurrency(c.montantInitialTotal)}</td><td class="text-right text-success">${formatCurrency(c.montantPayeTotal)}</td><td class="text-center">${c.tauxPaiement.toFixed(1)}%</td></tr>`).join("")}
${top5Contribuables.length === 0 ? '<tr><td colspan="5" class="text-center">Aucune donnée</td></tr>' : ""}
</tbody></table></div>
<div class="section"><div class="section-title">3. ÉVOLUTION MENSUELLE</div>
<table><thead><tr><th>Mois</th><th class="text-right">Montant</th></tr></thead><tbody>
${evolution.map((item) => `<tr><td>${item.mois}</td><td class="text-right">${formatCurrency(item.montant)}</td></tr>`).join("")}
${evolution.length === 0 ? '<tr><td colspan="2" class="text-center">Aucune donnée</td></tr>' : ""}
</tbody></table></div>
<div class="section"><div class="section-title">4. CLASSEMENT DES AGENTS</div>
<table><thead><tr><th class="text-center">Rang</th><th>Agent</th><th class="text-center">Total</th><th class="text-center">Auj.</th><th class="text-center">Hier</th></tr></thead><tbody>
${agentsList.slice(0, 20).map((agent) => `<tr><td class="text-center">${agent.rang}</td><td>${agent.nom_complet || "-"}</td><td class="text-center">${agent.total_enroles || 0}</td><td class="text-center">${agent.aujourdhui || 0}</td><td class="text-center">${agent.hier || 0}</td></tr>`).join("")}
${agentsList.length === 0 ? '<tr><td colspan="5" class="text-center">Aucune donnée</td></tr>' : ""}
</tbody></table></div>
<div class="footer"><div>SYNCTAXE - Système de gestion fiscale</div><div>Document généré automatiquement - Rapport complet</div></div>
</body></html>`;
  };

  const handleExportPDF = () => {
    setExporting(true);
    const content = generatePDFContent();
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute"; iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "none";
    document.body.appendChild(iframe);
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open(); iframeDoc.write(content); iframeDoc.close();
    iframe.contentWindow.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.print();
        setTimeout(() => {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          setExporting(false);
          showAlert("PDF généré avec succès", "success");
        }, 1000);
      }, 500);
    };
  };

  const handleExportExcel = async () => {
    const XLSX = await import("xlsx");
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      const stats = rapportData.stats_globaux || {};
      const contribuablesList = rapportData.contribuables?.data || [];
      const agentsList = rapportData.enrollement?.parAgent || [];
      const evolution = rapportData.evolution || [];
      const summaryData = [
        { Indicateur: "Total Contribuables", Valeur: stats.totalContribuables || 0 },
        { Indicateur: "Taxe Prévisionnelle", Valeur: formatCurrency(stats.totalAttendu || 0) },
        { Indicateur: "Total des Taxes Collectées", Valeur: formatCurrency(stats.totalPaye || 0) },
        { Indicateur: "Taxes Non Collectées", Valeur: formatCurrency(stats.totalRestant || 0) },
        { Indicateur: "Taux de Recouvrement", Valeur: `${stats.tauxRecouvrement?.toFixed(2) || 0}%` },
        { Indicateur: "Date de génération", Valeur: new Date().toLocaleString("fr-FR") },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), "Résumé");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(evolution.map((e) => ({ Mois: e.mois, Montant: formatCurrency(e.montant) }))), "Évolution mensuelle");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(contribuablesList.map((c) => ({ ID: c.contribuable_id, Nom: c.nom, Téléphone: c.telephone, Email: c.email, Souscriptions: c.nombreSouscriptions, "Total à payer": formatCurrency(c.montantInitialTotal), "Déjà payé": formatCurrency(c.montantPayeTotal), "Reste à payer": formatCurrency(c.montantRestant), "Taux (%)": c.tauxPaiement.toFixed(2) }))), "Tous les Contribuables");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(agentsList.map((agent) => ({ Rang: agent.rang, "ID Agent": agent.user_id, "Nom complet": agent.nom_complet, Email: agent.email, Téléphone: agent.telephone, Matricule: agent.matricule_interne || "", "Total enrôlements": agent.total_enroles, "Aujourd'hui": agent.aujourdhui || 0, Hier: agent.hier || 0, "Date création": formatDate(agent.created_at) }))), "Tous les Agents");
      XLSX.writeFile(wb, `rapport_complet_${new Date().toISOString().split("T")[0]}.xlsx`);
      showAlert("Export Excel réussi", "success");
    } catch (error) {
      console.error("Erreur export Excel:", error);
      showAlert("Erreur lors de l'export Excel", "danger");
    } finally { setExporting(false); }
  };

  const handleExportCSV = () => {
    setExporting(true);
    try {
      const contribuablesList = rapportData.contribuables?.data || [];
      const contribuablesData = contribuablesList.map((c) => ({
        ID: c.contribuable_id, Nom: c.nom, Téléphone: c.telephone, Email: c.email,
        Souscriptions: c.nombreSouscriptions,
        "Total à payer": c.montantInitialTotal, "Déjà payé": c.montantPayeTotal,
        "Reste à payer": c.montantRestant, "Taux (%)": c.tauxPaiement.toFixed(2),
      }));
      if (contribuablesData.length > 0) {
        const columns = Object.keys(contribuablesData[0]);
        const csvRows = [columns.join(";")];
        contribuablesData.forEach((row) => {
          const values = columns.map((col) => {
            let value = row[col];
            if (typeof value === "string" && (value.includes(";") || value.includes('"'))) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          });
          csvRows.push(values.join(";"));
        });
        const blob = new Blob(["\uFEFF" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", `tous_contribuables_${new Date().toISOString().split("T")[0]}.csv`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
        showAlert("Export CSV réussi", "success");
      } else {
        showAlert("Aucune donnée à exporter", "warning");
      }
    } catch (error) {
      console.error("Erreur export CSV:", error);
      showAlert("Erreur lors de l'export CSV", "danger");
    } finally { setExporting(false); }
  };

  const handleExportExcelParJour = async () => {
    const XLSX = await import("xlsx");
    setExporting(true);
    try {
      const journalierRaw = await fetchJournalierForPeriod();
      const daily = journalierRaw.map(formatJournalierRow);
      const wb = XLSX.utils.book_new();
      const totalAttendu = daily.reduce((s, d) => s + d.attendu, 0);
      const totalCollecte = daily.reduce((s, d) => s + d.collecte, 0);
      const totalOperations = daily.reduce((s, d) => s + d.nombreCollectes, 0);
      const joursActifs = daily.filter((d) => d.collecte > 0).length;
      const moyenneJour = joursActifs > 0 ? totalCollecte / joursActifs : 0;
      const tauxGlobal = totalAttendu > 0 ? round2((totalCollecte / totalAttendu) * 100) : 0;
      const meilleurJour = daily.reduce((best, d) => (d.collecte > (best?.collecte || 0) ? d : best), null);
      const resumeData = [
        { Indicateur: "Période", Valeur: `${dateRange.startDate || "Début"} → ${dateRange.endDate || "Aujourd'hui"}` },
        { Indicateur: "Nombre de jours couverts", Valeur: daily.length },
        { Indicateur: "Jours avec collecte", Valeur: joursActifs },
        { Indicateur: "Montant attendu total", Valeur: formatCurrency(totalAttendu) },
        { Indicateur: "Montant collecté total", Valeur: formatCurrency(totalCollecte) },
        { Indicateur: "Taux global de collecte", Valeur: `${tauxGlobal}%` },
        { Indicateur: "Total opérations", Valeur: totalOperations },
        { Indicateur: "Moyenne collectée / jour actif", Valeur: formatCurrency(moyenneJour) },
        { Indicateur: "Meilleur jour", Valeur: meilleurJour ? `${meilleurJour.dateLabel} — ${formatCurrency(meilleurJour.collecte)}` : "-" },
        { Indicateur: "Date de génération", Valeur: new Date().toLocaleString("fr-FR") },
      ];
      const resumeSheet = XLSX.utils.json_to_sheet(resumeData);
      resumeSheet["!cols"] = [{ wch: 35 }, { wch: 45 }];
      XLSX.utils.book_append_sheet(wb, resumeSheet, "Résumé période");
      const detailData = daily.map((d, idx) => ({
        Jour: idx + 1, Date: d.dateLabel, "Jour de la semaine": d.jourSemaine,
        "Montant attendu": d.attendu, "Montant attendu (formaté)": formatCurrency(d.attendu),
        "Montant collecté": d.collecte, "Montant collecté (formaté)": formatCurrency(d.collecte),
        "Pourcentage collecté (%)": d.pourcentage, "Nombre de collectes": d.nombreCollectes,
      }));
      const detailSheet = XLSX.utils.json_to_sheet(detailData);
      detailSheet["!cols"] = [{ wch: 6 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 24 }, { wch: 16 }, { wch: 24 }, { wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, detailSheet, "Détail par jour");
      const totalRow = [{
        Date: "TOTAL", "Montant attendu": totalAttendu, "Montant attendu (formaté)": formatCurrency(totalAttendu),
        "Montant collecté": totalCollecte, "Montant collecté (formaté)": formatCurrency(totalCollecte),
        "Pourcentage collecté (%)": tauxGlobal, "Nombre de collectes": totalOperations,
      }];
      const totalSheet = XLSX.utils.json_to_sheet(totalRow);
      totalSheet["!cols"] = [{ wch: 12 }, { wch: 16 }, { wch: 24 }, { wch: 16 }, { wch: 24 }, { wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, totalSheet, "Total");
      XLSX.writeFile(wb, `rapport_journalier_${dateRange.startDate || "debut"}_${dateRange.endDate || "fin"}.xlsx`);
      showAlert("Export Excel journalier réussi", "success");
    } catch (error) {
      console.error("Erreur export Excel journalier:", error);
      showAlert("Erreur lors de l'export Excel journalier", "danger");
    } finally { setExporting(false); }
  };

  const generatePDFJournalier = (daily) => {
    const dateGeneration = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const totalAttendu = daily.reduce((s, d) => s + d.attendu, 0);
    const totalCollecte = daily.reduce((s, d) => s + d.collecte, 0);
    const totalOperations = daily.reduce((s, d) => s + d.nombreCollectes, 0);
    const joursActifs = daily.filter((d) => d.collecte > 0).length;
    const moyenneJour = joursActifs > 0 ? totalCollecte / joursActifs : 0;
    const tauxGlobal = totalAttendu > 0 ? Math.round((totalCollecte / totalAttendu) * 10000) / 100 : 0;
    const meilleurJour = daily.reduce((best, d) => (d.collecte > (best?.collecte || 0) ? d : best), null);
    const lignes = daily.map((d, idx) => `
      <tr><td class="text-center">${idx + 1}</td><td class="fw-semibold">${d.dateLabel}</td>
        <td style="font-size:8px;color:#888;">${d.jourSemaine}</td>
        <td class="text-right">${formatCurrency(d.attendu)}</td><td class="text-right">${formatCurrency(d.collecte)}</td>
        <td class="text-center"><div style="background:#e9ecef;height:6px;width:70px;margin:0 auto 3px auto;">
          <div style="background:${d.pourcentage >= 80 ? "#28a745" : d.pourcentage >= 50 ? "#ffc107" : "#dc3545"};width:${Math.min(d.pourcentage, 100)}%;height:6px;"></div></div>
          <span style="font-size:8px;font-weight:600;">${d.pourcentage.toFixed(1)}%</span></td>
        <td class="text-center">${d.nombreCollectes}</td></tr>`).join("");
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Rapport journalier - SyncTaxe</title>
<style>* { margin:0; padding:0; box-sizing:border-box; } body { font-family:'Segoe UI', Arial, sans-serif; font-size:11px; padding:20px; background:white; }
@media print { body { padding:10px; } }
.header { text-align:center; margin-bottom:25px; padding-bottom:15px; border-bottom:3px solid #1a3c5e; } .header h1 { font-size:22px; color:#1a1a1a; margin-bottom:5px; } .header .subtitle { font-size:11px; color:#666; } .header .date { font-size:9px; color:#888; margin-top:5px; }
.section-title { font-size:14px; font-weight:600; color:#1a3c5e; margin-bottom:10px; padding-bottom:4px; border-bottom:2px solid #1a3c5e; }
.stats-row { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; } .stat-box { flex:1; min-width:120px; padding:12px; background:#f5f5f5; border-left:3px solid #1a3c5e; text-align:center; }
.stat-number { font-size:15px; font-weight:700; color:#1a3c5e; } .stat-label { font-size:9px; color:#666; text-transform:uppercase; }
table { width:100%; border-collapse:collapse; margin-bottom:10px; } th { background:#1a3c5e; color:white; padding:8px 10px; text-align:left; font-size:10px; } td { padding:6px 10px; border-bottom:1px solid #ddd; font-size:9px; }
.text-center { text-align:center; } .text-right { text-align:right; } .footer { margin-top:25px; padding-top:10px; border-top:1px solid #ddd; text-align:center; font-size:8px; color:#888; }
</style></head><body>
<div class="header"><h1>SYNCTAXE</h1><div class="subtitle">Système de gestion fiscale</div><div class="subtitle">Rapport journalier des collectes</div>
<div class="date">Généré le ${dateGeneration}</div><div class="date">Période : ${dateRange.startDate || "Début"} → ${dateRange.endDate || "Aujourd'hui"}</div>
${canUseMarketScope ? `<div class="date">Périmètre : ${marketScope === "all" ? "Tous" : marketScope === "no_market" ? "Hors marché" : "De marché"}${isSuperAdmin ? " (Super Admin)" : ""}</div>` : ""}</div>
<div class="section-title">1. SYNTHÈSE DE LA PÉRIODE</div>
<div class="stats-row"><div class="stat-box"><div class="stat-number">${daily.length}</div><div class="stat-label">JOURS COUVERTS</div></div>
<div class="stat-box"><div class="stat-number">${formatCurrency(totalAttendu)}</div><div class="stat-label">MONTANT ATTENDU</div></div>
<div class="stat-box"><div class="stat-number">${formatCurrency(totalCollecte)}</div><div class="stat-label">MONTANT COLLECTÉ</div></div>
<div class="stat-box"><div class="stat-number">${tauxGlobal}%</div><div class="stat-label">TAUX GLOBAL</div></div></div>
<table><thead><tr><th>Indicateur</th><th class="text-right">Valeur</th></tr></thead><tbody>
<tr><td>Total opérations</td><td class="text-right">${totalOperations}</td></tr>
<tr><td>Jours avec collecte</td><td class="text-right">${joursActifs}</td></tr>
<tr><td>Moyenne collectée / jour actif</td><td class="text-right">${formatCurrency(moyenneJour)}</td></tr>
<tr><td>Meilleur jour</td><td class="text-right">${meilleurJour ? `${meilleurJour.dateLabel} — ${formatCurrency(meilleurJour.collecte)}` : "-"}</td></tr>
</tbody></table>
<div class="section-title" style="margin-top:20px;">2. DÉTAIL PAR JOUR — ATTENDU / COLLECTÉ / %</div>
<table><thead><tr><th class="text-center" style="width:30px;">#</th><th style="width:75px;">Date</th><th style="width:65px;">Jour</th>
<th class="text-right">Montant attendu</th><th class="text-right">Montant collecté</th>
<th class="text-center" style="width:110px;">% collecté</th><th class="text-center" style="width:70px;">Opérations</th></tr></thead>
<tbody>${lignes || '<tr><td colspan="7" class="text-center">Aucune donnée disponible</td></tr>'}</tbody>
<tfoot><tr style="background:#f0f0f0; font-weight:700;"><td colspan="3" class="text-right">TOTAL</td>
<td class="text-right">${formatCurrency(totalAttendu)}</td><td class="text-right">${formatCurrency(totalCollecte)}</td>
<td class="text-center">${tauxGlobal}%</td><td class="text-center">${totalOperations}</td></tr></tfoot></table>
<div class="footer"><div>SYNCTAXE - Système de gestion fiscale</div><div>Document généré automatiquement - Rapport journalier</div></div>
</body></html>`;
  };

  const handleExportPDFParJour = async () => {
    setExporting(true);
    try {
      const journalierRaw = await fetchJournalierForPeriod();
      const daily = journalierRaw.map(formatJournalierRow);
      const content = generatePDFJournalier(daily);
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute"; iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "none";
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open(); iframeDoc.write(content); iframeDoc.close();
      iframe.contentWindow.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.print();
          setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
            setExporting(false);
            showAlert("PDF journalier généré avec succès", "success");
          }, 1000);
        }, 500);
      };
    } catch (error) {
      console.error("Erreur export PDF journalier:", error);
      showAlert("Erreur lors de l'export PDF journalier", "danger");
      setExporting(false);
    }
  };

  // ==================== PROCÈS-VERBAUX ====================

  const buildPVData = () => {
    const agents = allAgentsForPV.length > 0 ? allAgentsForPV : (rapportData.enrollement?.parAgent || []);

    const agentsData = agents.map((agent) => {
      // 1. Récupération des détails depuis l'API (grâce à la modif PHP)
      const collectes = agent.collectes_details || [];

      // 2. Formatage des lignes pour le tableau
      const lignes = collectes.map((c) => ({
        contribuable: c.nom_contribuable || "-",
        adresse: c.adresse || "-",
        contact: c.telephone || "-",
        montant: parseFloat(c.montant || 0),
        date: c.date_collecte ? new Date(c.date_collecte).toLocaleDateString("fr-FR") : "-",
      }));

      // 3. Calcul du total pour cet agent
      const totalAgent = lignes.reduce((sum, l) => sum + l.montant, 0);

      return {
        agent_id: agent.user_id,
        nom_complet: agent.nom_complet || "-",
        matricule: cleanMatricule(agent),
        lignes: lignes,
        total: totalAgent,
      };
    });

    return {
      periode: { debut: dateRange.startDate || "", fin: dateRange.endDate || "" },
      lieu_auto: extractLieuFromEntiteName(rapportData.enrollement?.entite_info?.nom || ""),
      agents: agentsData,
      total_genere_le: new Date().toLocaleString("fr-FR"),
    };
  };

  const handleOpenPVModal = async () => {
    setLoadingPV(true);
    try {
      await loadAllAgentsForPV();
      setTimeout(() => {
        const data = buildPVData();
        if (!data.agents || data.agents.length === 0) {
          showAlert("Aucun agent trouvé pour la période sélectionnée. Impossible de générer un PV.", "warning");
          setLoadingPV(false);
          return;
        }
        setPvMeta((prev) => ({ ...prev, lieu: data.lieu_auto || prev.lieu }));
        setPvData(data);
        setPvModalOpen(true);
        setLoadingPV(false);
      }, 100);
    } catch (error) {
      console.error("Erreur ouverture PV:", error);
      showAlert("Erreur lors du chargement du PV", "danger");
      setLoadingPV(false);
    }
  };

  const generatePVHtml = (data, meta, logos = {}) => {
    const fmtDate = (d) => {
      if (!d) return "…. / …. /……";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "…. / …. /……";
      return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
    };

    const fmtMontant = (n) => {
      const num = parseFloat(n) || 0;
      return num.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const { logoLeft = "", logoRight = "" } = logos;

    // Génération des sections par agent
    // ✅ Saut de page entre les agents uniquement (pas après le dernier),
    //    pour que les signatures finales restent sur la dernière page et
    //    qu'aucune page blanche ne soit générée.
    const agentsSectionsHtml = data.agents.map((agent, index) => {
      const isLast = index === data.agents.length - 1;

      const rowsHtml = agent.lignes.map((l) => `
        <tr>
          <td>${l.date}</td>
          <td>${l.contribuable}</td>
          <td>${l.adresse}</td>
          <td>${l.contact}</td>
          <td class="text-right">${fmtMontant(l.montant)}</td>
        </tr>
      `).join("");

      return `
        <div class="agent-section" style="margin-top: 25px; ${isLast ? "" : "page-break-after: always; break-after: page;"}">
          <div style="font-weight: bold; margin-bottom: 8px; font-size: 11pt;">
            Historique de collecte : ${agent.nom_complet} ${agent.matricule ? `(${agent.matricule})` : ""}
          </div>

          <table class="agent-table">
            <thead>
              <tr>
                <th style="width: 15%;">DATE</th>
                <th style="width: 30%;">CONTRIBUABLE</th>
                <th style="width: 30%;">ADRESSE</th>
                <th style="width: 15%;">CONTACT</th>
                <th style="width: 10%;" class="text-right">MONTANT</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || `<tr><td colspan="5" class="text-center" style="font-style: italic;">Aucune collecte pour cet agent sur la période.</td></tr>`}
              <tr class="total-row" style="background-color: #f8f9fa;">
                <td colspan="4" class="text-right" style="font-weight: bold;">TOTAL COLLECTÉ :</td>
                <td class="text-right" style="font-weight: bold;">${fmtMontant(agent.total)}</td>
              </tr>
            </tbody>
          </table>

          <div class="signature-box">
            Signature :
          </div>
        </div>
      `;
    }).join("");

    return `
<div class="pv-container">

  <div class="pv-header-logos" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
    ${logoLeft ? `<img src="${logoLeft}" alt="Logo" style="height: 50px; object-fit: contain;" />` : ``}
    ${logoRight ? `<img src="${logoRight}" alt="Logo" style="height: 50px; object-fit: contain;" />` : ``}
  </div>

  <table class="main-title-table">
    <tr>
      <td style="text-align: center; font-weight: bold; text-transform: uppercase;">PROCÈS-VERBAL RÉCAPITULATIF DE COLLECTE DES TAXES</td>
    </tr>
    <tr>
      <td style="text-align: center; font-size: 10pt; padding: 5px;">
        Période : Du ${fmtDate(data.periode.debut)} au ${fmtDate(data.periode.fin)}
      </td>
    </tr>
  </table>

  ${agentsSectionsHtml}

  <div style="margin-top: 40px; text-align: right; font-size: 11pt; page-break-inside: avoid; break-inside: avoid;">
    <p>Fait à ${meta.lieu || data.lieu_auto || "…………"}, le ${fmtDate(meta.dateValidation)}</p>
  </div>

  <div class="signatures-table" style="margin-top: 30px; display: flex; justify-content: space-between; page-break-inside: avoid; break-inside: avoid;">
    <div class="signature-col" style="width: 45%; text-align: center;">
      <div style="font-weight: bold; margin-bottom: 50px;">Le Responsable de la Collecte</div>
      <div style="border-top: 1px solid #000; width: 80%; margin: 0 auto;"></div>
    </div>
    <div class="signature-col" style="width: 45%; text-align: center;">
      <div style="font-weight: bold; margin-bottom: 50px;">Le Responsable Financier</div>
      <div style="border-top: 1px solid #000; width: 80%; margin: 0 auto;"></div>
    </div>
  </div>

</div>`;
  };

  const ensureLogosReady = async () => {
    if (logosReady) return;
    setLoadingPV(true);
    try {
      const [logoLeft, logoRight] = await Promise.all([
        loadImageAsBase64("/img/logominerve.png").catch((e) => {
          console.warn("Logo gauche non chargé :", e.message);
          return "";
        }),
        loadImageAsBase64("/img/image.jpeg").catch((e) => {
          console.warn("Logo droit non chargé :", e.message);
          return "";
        }),
      ]);
      setPvLogos({ logoLeft, logoRight });
      setLogosReady(true);
    } finally {
      setLoadingPV(false);
    }
  };

  const handleExportPVPdf = async () => {
    if (!pvData) return;
    await ensureLogosReady();
    setPvModalOpen(false);
    setPvPreviewOpen(true);
  };

  const handlePreviewPV = async () => {
    if (!pvData) return;
    await ensureLogosReady();
    setPvModalOpen(false);
    setPvPreviewOpen(true);
  };

  const handlePrintPVFromModal = async () => {
    if (!pvData) return;

    await ensureLogosReady();

    let logoLeft = "", logoRight = "";
    try {
      [logoLeft, logoRight] = await Promise.all([
        loadImageAsBase64("/img/logominerve.png").catch((e) => {
          console.warn("Logo gauche non chargé :", e.message);
          return "";
        }),
        loadImageAsBase64("/img/image.jpeg").catch((e) => {
          console.warn("Logo droit non chargé :", e.message);
          return "";
        }),
      ]);
    } catch (e) {
      console.warn("Erreur chargement logos :", e);
    }

    // ✅ La numérotation « Page X / Y » est gérée nativement par @page
    //    (voir le CSS ci-dessous) : plus d'insertion manuelle dans le HTML.
    const pvHtml = generatePVHtml(pvData, pvMeta, { logoLeft, logoRight });

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:none;";
    document.body.appendChild(iframe);
    const iframeDoc = iframe.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Procès-verbal récapitulatif de collecte des taxes</title>
  <style>
    /* ✅ Pagination native en bas de chaque page : « Page X / Y »
       (Chrome / Edge 131+). */
    @page {
      size: A4;
      margin: 15mm 15mm 20mm 15mm;

      @bottom-center {
        content: "Page " counter(page) " / " counter(pages);
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        color: #555;
      }
    }

    * { margin: 0; padding: 0; box-sizing: border-box; }

    html, body {
      background: #fff;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11pt;
      color: #000;
      line-height: 1.3;
    }

    .pv-container { width: 100%; }

    /* Tableau principal (Titre) */
    .main-title-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    .main-title-table td {
      border: 1px solid #000;
      padding: 8px;
      font-size: 11pt;
    }

    /* Tableau par agent */
    .agent-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10pt;
      margin-bottom: 0;
    }
    .agent-table th {
      border: 1px solid #000;
      padding: 8px;
      text-align: left;
      font-weight: bold;
      background-color: #fff;
      text-transform: uppercase;
    }
    .agent-table td {
      border: 1px solid #000;
      padding: 8px;
      vertical-align: top;
    }
    .agent-table .text-right { text-align: right; }
    .agent-table .text-center { text-align: center; }

    /* ✅ Si le tableau d'un agent déborde sur plusieurs pages :
       l'en-tête est répété et une ligne n'est jamais coupée en deux */
    .agent-table thead { display: table-header-group; }
    .agent-table tr { page-break-inside: avoid; break-inside: avoid; }

    /* Case signature sous chaque agent */
    .signature-box {
      border: 1px solid #000;
      border-top: none;
      padding: 12px;
      font-size: 10pt;
      min-height: 50px;
      margin-bottom: 20px;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    /* Signatures finales */
    .signatures-table {
      display: flex;
      justify-content: space-between;
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
  ${pvHtml}
</body>
</html>`);
    iframeDoc.close();

    const waitForImages = () => {
      const imgs = iframeDoc.images;
      if (!imgs || imgs.length === 0) return Promise.resolve();
      return Promise.all(
        Array.from(imgs).map(
          (img) =>
            new Promise((res) => {
              if (img.complete && img.naturalHeight > 0) return res();
              img.onload = () => res();
              img.onerror = () => res();
            })
        )
      );
    };

    await waitForImages();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 1500);
    }, 300);
  };

  const handleExportPVWord = () => {
    showAlert("Export Word temporairement désactivé. Utilisez PDF ou l'aperçu imprimable.", "warning");
  };

  // ==================== FIN PROCÈS-VERBAUX ====================

  const statsGlobaux = rapportData.stats_globaux || {};
  const evolution = rapportData.evolution || [];
  const enrollement = rapportData.enrollement || { global: null, parAgent: [], totauxAgents: null, pagination: {} };

  const maxMontant = useMemo(() => {
    if (evolution.length === 0) return 1;
    return Math.max(...evolution.map((e) => e.montant), 1);
  }, [evolution]);

  const top5Contribuables = useMemo(() => {
    const data = rapportData.contribuables?.data || [];
    return [...data].sort((a, b) => b.montantInitialTotal - a.montantInitialTotal).slice(0, 5);
  }, [rapportData.contribuables?.data]);

  const marketScopeLabel =
    marketScope === "all" ? "Tous les contribuables"
      : marketScope === "no_market" ? "Hors marché uniquement"
      : "De marché uniquement";

  const marketScopeBadgeColor =
    marketScope === "all" ? "secondary"
      : marketScope === "no_market" ? "primary"
      : "success";

  return (
    <React.Fragment>
      <Header />

      <ToastContainer className="p-3" style={{ zIndex: 9999, position: "fixed", top: "20px", right: "20px" }}>
        <Toast show={alert.show} onClose={() => setAlert((prev) => ({ ...prev, show: false }))} bg={alert.type} delay={4000} autohide>
          <Toast.Header><strong className="me-auto">Notification</strong></Toast.Header>
          <Toast.Body className="text-white">{alert.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="main main-app p-3 p-lg-4" style={{ background: "#f5f7fa", minHeight: "100vh" }}>
        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h4 className="mb-0 d-flex align-items-center">
                    <FileEarmarkBarGraph className="me-2 text-primary" /> Rapports et Statistiques
                  </h4>
                  <p className="text-muted mb-0">Analyse détaillée des collectes et des contribuables</p>
                </div>
                <div className="d-flex gap-2">
                  <Dropdown>
                    <Dropdown.Toggle variant="success" size="sm" disabled={loading || exporting}>
                      {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : <Download className="me-2" />}
                      Exporter
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleExportPDF} disabled={loading || exporting}><FileEarmarkPdf className="me-2 text-danger" /> PDF global</Dropdown.Item>
                      <Dropdown.Item onClick={handleExportPDFParJour} disabled={loading || exporting}><FileEarmarkPdf className="me-2 text-danger" /> PDF par jour</Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleExportExcel} disabled={loading || exporting}><FiletypeXlsx className="me-2 text-success" /> Excel global</Dropdown.Item>
                      <Dropdown.Item onClick={handleExportExcelParJour} disabled={loading || exporting}><FiletypeXlsx className="me-2 text-success" /> Excel par jour</Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleExportCSV} disabled={loading || exporting}><FiletypeCsv className="me-2 text-primary" /> CSV</Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleOpenPVModal} disabled={loading || exporting || loadingPV}><FileEarmarkText className="me-2 text-primary" /> Procès-verbal</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button variant="outline-primary" size="sm" onClick={() => loadAllData()} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <ClockHistory className="me-2" />}
                    {loading ? "Chargement..." : "Actualiser"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Filtres dates + périmètre marché */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <Row className="align-items-center g-3">
                    <Col md={canUseMarketScope ? 3 : 4}>
                      <Form.Label className="fw-semibold mb-1 small">Période rapide</Form.Label>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant={dateRange.period === 7 ? "primary" : "outline-primary"} onClick={() => updatePeriod(7)}>7 jours</Button>
                        <Button size="sm" variant={dateRange.period === 30 ? "primary" : "outline-primary"} onClick={() => updatePeriod(30)}>30 jours</Button>
                        <Button size="sm" variant={dateRange.period === 90 ? "primary" : "outline-primary"} onClick={() => updatePeriod(90)}>90 jours</Button>
                      </div>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="fw-semibold mb-1 small">Date de début</Form.Label>
                      <Form.Control type="date" size="sm" value={dateRange.startDate} onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value, period: "custom" }))} />
                    </Col>
                    <Col md={2}>
                      <Form.Label className="fw-semibold mb-1 small">Date de fin</Form.Label>
                      <Form.Control type="date" size="sm" value={dateRange.endDate} onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value, period: "custom" }))} />
                    </Col>
                    {canUseMarketScope && (
                      <Col md={3}>
                        <Form.Label className="fw-semibold mb-1 small"><Shop size={12} className="me-1" />Périmètre marché</Form.Label>
                        <Form.Select size="sm" value={marketScope} onChange={(e) => setMarketScope(e.target.value)}>
                          <option value="all">Tous les contribuables</option>
                          <option value="no_market">Hors marché uniquement</option>
                          <option value="market">De marché uniquement</option>
                        </Form.Select>
                      </Col>
                    )}
                    <Col md={2}>
                      <Form.Label className="fw-semibold mb-1 small">&nbsp;</Form.Label>
                      <Button variant="outline-secondary" size="sm" className="w-100" onClick={resetDateRange}>
                        <ClockHistory className="me-1" size={14} /> Mois en cours
                      </Button>
                    </Col>
                  </Row>
                  <div className="mt-2 text-center">
                    {(dateRange.startDate || dateRange.endDate) && (
                      <Badge bg="info" className="px-3 py-2 me-2">
                        <Calendar className="me-1" size={12} /> Période : {dateRange.startDate || "Début"} → {dateRange.endDate || "Aujourd'hui"}
                      </Badge>
                    )}
                    {canUseMarketScope && (
                      <Badge bg={marketScopeBadgeColor} className="px-3 py-2">
                        <Shop className="me-1" size={12} /> {isSuperAdmin ? "Super Admin • " : ""}Périmètre : {marketScopeLabel}
                      </Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Cartes statistiques */}
          <Row className="g-3 mb-4">
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm h-100 bg-white position-relative overflow-hidden stats-card">
                <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #0d6efd, #0a58ca)" }} />
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 38, height: 38, backgroundColor: "rgba(13, 110, 253, 0.1)" }}>
                      <People className="text-primary" size={19} strokeWidth={1.5} />
                    </div>
                    {!loading && statsGlobaux.registeredToday > 0 && (
                      <div className="d-flex align-items-center px-2 py-1 rounded-pill bg-success-subtle text-success" style={{ fontSize: "11px", fontWeight: 500 }}>
                        <ArrowUp size={10} className="me-1" /><span>+{statsGlobaux.registeredToday}</span>
                      </div>
                    )}
                  </div>
                  {loading ? (
                    <div className="mt-2"><Placeholder as="div" animation="glow"><Placeholder xs={6} style={{ height: "30px", borderRadius: "8px" }} className="mb-2" /><Placeholder xs={8} style={{ height: "14px", borderRadius: "4px" }} /></Placeholder></div>
                  ) : (
                    <>
                      <div className="mt-2"><span className="fw-bold" style={{ fontSize: "21px", color: "#1e293b" }}>{statsGlobaux.totalContribuables?.toLocaleString() || 0}</span></div>
                      <div className="mt-1"><span className="text-uppercase fw-semibold" style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.5px" }}>Total Contribuables</span></div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm h-100 bg-white position-relative overflow-hidden stats-card">
                <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #198754, #157347)" }} />
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 38, height: 38, backgroundColor: "rgba(25, 135, 84, 0.1)" }}>
                      <CurrencyDollar className="text-success" size={19} strokeWidth={1.5} />
                    </div>
                  </div>
                  {loading ? (
                    <div className="mt-2"><Placeholder as="div" animation="glow"><Placeholder xs={6} style={{ height: "30px", borderRadius: "8px" }} className="mb-2" /><Placeholder xs={8} style={{ height: "14px", borderRadius: "4px" }} /></Placeholder></div>
                  ) : (
                    <>
                      <div className="mt-2"><span className="fw-bold" style={{ fontSize: "19px", color: "#1e293b" }}>{formatCurrency(statsGlobaux.totalAttendu || 0)}</span></div>
                      <div className="mt-1"><span className="text-uppercase fw-semibold" style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.5px" }}>Taxe Prévisionnelle</span></div>
                      <div className="mt-2 small text-muted">{statsGlobaux.echeancesPayees || 0} soldées • {statsGlobaux.echeancesEnAttente || 0} en attente</div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm h-100 bg-white position-relative overflow-hidden stats-card">
                <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #ffc107, #e0a800)" }} />
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 38, height: 38, backgroundColor: "rgba(255, 193, 7, 0.1)" }}>
                      <Receipt className="text-warning" size={19} strokeWidth={1.5} />
                    </div>
                    {!loading && (
                      <div className="d-flex align-items-center px-2 py-1 rounded-pill bg-info-subtle text-info" style={{ fontSize: "11px", fontWeight: 500 }}>
                        <GraphUp size={10} className="me-1" /><span>Taux: {statsGlobaux.tauxRecouvrement?.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                  {loading ? (
                    <div className="mt-2"><Placeholder as="div" animation="glow"><Placeholder xs={6} style={{ height: "25px", borderRadius: "8px" }} className="mb-2" /><Placeholder xs={8} style={{ height: "14px", borderRadius: "4px" }} /></Placeholder></div>
                  ) : (
                    <>
                      <div className="mt-2"><span className="fw-bold" style={{ fontSize: "19px", color: "#1e293b" }}>{formatCurrency(statsGlobaux.totalPaye || 0)}</span></div>
                      <div className="mt-1"><span className="text-uppercase fw-semibold" style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.5px" }}>Total Collecté</span></div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm h-100 bg-white position-relative overflow-hidden stats-card">
                <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #dc3545, #c82333)" }} />
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 38, height: 38, backgroundColor: "rgba(220, 53, 69, 0.1)" }}>
                      <XCircle className="text-danger" size={19} strokeWidth={1.5} />
                    </div>
                    {!loading && statsGlobaux.echeancesPartielles > 0 && (
                      <div className="d-flex align-items-center px-2 py-1 rounded-pill bg-danger-subtle text-danger" style={{ fontSize: "11px", fontWeight: 500 }}>
                        <ExclamationCircle size={10} className="me-1" /><span>{statsGlobaux.echeancesPartielles} partielles</span>
                      </div>
                    )}
                  </div>
                  {loading ? (
                    <div className="mt-2"><Placeholder as="div" animation="glow"><Placeholder xs={6} style={{ height: "25px", borderRadius: "8px" }} className="mb-2" /><Placeholder xs={8} style={{ height: "14px", borderRadius: "4px" }} /></Placeholder></div>
                  ) : (
                    <>
                      <div className="mt-2"><span className="fw-bold" style={{ fontSize: "19px", color: "#1e293b" }}>{formatCurrency(statsGlobaux.totalRestant || 0)}</span></div>
                      <div className="mt-1"><span className="text-uppercase fw-semibold" style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.5px" }}>Reste à Payer</span></div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Onglets */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white">
                  <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-0 custom-tabs">
                    <Tab eventKey="apercu" title={<span><PieChart className="me-2" size={16} />Aperçu général</span>} />
                    <Tab eventKey="contribuables" title={<span><People className="me-2" size={16} />Situation des contribuables</span>} />
                    <Tab eventKey="enrollement" title={<span><Trophy className="me-2" size={16} />Statistiques d'enrôlement</span>} />
                    <Tab eventKey="proces-verbaux" title={<span><FileEarmarkText className="me-2" size={16} />Procès-Verbaux</span>} />
                  </Tabs>
                </Card.Header>
              </Card>
            </Col>
          </Row>

          {/* ONGLET APERÇU */}
          {activeTab === "apercu" && (
            <>
              <Row className="g-4 mb-4">
                <Col md={8}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Header className="bg-light"><h6 className="mb-0"><BarChart className="me-2" />Évolution mensuelle des collectes</h6></Card.Header>
                    <Card.Body>
                      {loading ? (
                        <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
                      ) : evolution.length === 0 ? (
                        <div className="text-center py-5"><GraphUp size={48} className="text-muted" /><p className="mt-2 text-muted">Aucune donnée disponible</p></div>
                      ) : (
                        <div className="evolution-chart" style={{ height: "300px", padding: "20px 0" }}>
                          <div className="d-flex align-items-end justify-content-around h-100">
                            {evolution.map((item, idx) => {
                              const height = (item.montant / maxMontant) * 200;
                              return (
                                <div key={idx} className="text-center" style={{ width: "80px" }}>
                                  <div className="bg-primary rounded-top mx-auto" style={{ height: `${height}px`, width: "40px", transition: "height 0.3s ease" }}>
                                    <div className="evolution-tooltip">{formatCurrency(item.montant)}</div>
                                  </div>
                                  <div className="mt-2 small text-muted">{item.mois}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Header className="bg-light"><h6 className="mb-0"><PieChart className="me-2" />Taux de recouvrement</h6></Card.Header>
                    <Card.Body className="text-center">
                      {loading ? (
                        <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
                      ) : (
                        <>
                          <div className="position-relative d-inline-block mx-auto mb-4">
                            <div className="progress-circle" style={{ width: "150px", height: "150px" }}>
                              <div className="progress-circle-value" style={{ background: `conic-gradient(#28a745 ${statsGlobaux.tauxRecouvrement || 0}%, #e9ecef ${statsGlobaux.tauxRecouvrement || 0}%)`, borderRadius: "50%", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <div className="bg-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: "110px", height: "110px" }}>
                                  <span className="h2 fw-bold mb-0">{Math.round(statsGlobaux.tauxRecouvrement || 0)}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row g-2">
                            <div className="col-4"><div className="bg-success bg-opacity-10 rounded p-2"><div className="small text-muted">Payé</div><div className="fw-bold">{Math.round((statsGlobaux.totalPaye / (statsGlobaux.totalAttendu || 1)) * 100) || 0}%</div></div></div>
                            <div className="col-4"><div className="bg-warning bg-opacity-10 rounded p-2"><div className="small text-muted">Restant</div><div className="fw-bold">{Math.round((statsGlobaux.totalRestant / (statsGlobaux.totalAttendu || 1)) * 100) || 0}%</div></div></div>
                            <div className="col-4"><div className="bg-info bg-opacity-10 rounded p-2"><div className="small text-muted">Taux</div><div className="fw-bold">{statsGlobaux.tauxRecouvrement?.toFixed(1) || 0}%</div></div></div>
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-4">
                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Header className="bg-light"><h6 className="mb-0"><Trophy className="me-2 text-warning" />Top 5 contribuables</h6></Card.Header>
                    <Card.Body>
                      {loading ? (
                        <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
                      ) : top5Contribuables.length === 0 ? (
                        <div className="text-center py-5"><Person size={48} className="text-muted" /><p className="mt-2 text-muted">Aucune donnée</p></div>
                      ) : (
                        <div className="table-responsive">
                          <Table hover size="sm">
                            <thead className="bg-light"><tr><th>#</th><th>Nom</th><th className="text-end">Total</th><th className="text-end">Payé</th><th className="text-center">Taux</th></tr></thead>
                            <tbody>
                              {top5Contribuables.map((c, idx) => (
                                <tr key={idx}>
                                  <td className="text-center">{idx === 0 ? <Trophy size={16} className="text-warning" /> : idx + 1}</td>
                                  <td>{c.nom}</td>
                                  <td className="text-end fw-bold">{formatCurrency(c.montantInitialTotal)}</td>
                                  <td className="text-end text-success">{formatCurrency(c.montantPayeTotal)}</td>
                                  <td className="text-center"><ProgressBar now={c.tauxPaiement} style={{ height: "4px", width: "80px" }} className="me-2" /><small>{Math.round(c.tauxPaiement)}%</small></td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Header className="bg-light"><h6 className="mb-0"><Receipt className="me-2" />Résumé des échéances</h6></Card.Header>
                    <Card.Body>
                      {loading ? (
                        <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
                      ) : (
                        <>
                          <div className="mb-3"><div className="d-flex justify-content-between mb-1"><span>Payées</span><span className="fw-bold text-success">{statsGlobaux.echeancesPayees || 0}</span></div><ProgressBar now={(statsGlobaux.echeancesPayees / (statsGlobaux.totalSouscriptions || 1)) * 100} variant="success" /></div>
                          <div className="mb-3"><div className="d-flex justify-content-between mb-1"><span>Partielles</span><span className="fw-bold text-warning">{statsGlobaux.echeancesPartielles || 0}</span></div><ProgressBar now={(statsGlobaux.echeancesPartielles / (statsGlobaux.totalSouscriptions || 1)) * 100} variant="warning" /></div>
                          <div className="mb-3"><div className="d-flex justify-content-between mb-1"><span>En attente</span><span className="fw-bold text-secondary">{statsGlobaux.echeancesEnAttente || 0}</span></div><ProgressBar now={(statsGlobaux.echeancesEnAttente / (statsGlobaux.totalSouscriptions || 1)) * 100} variant="secondary" /></div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* ONGLET CONTRIBUABLES */}
          {activeTab === "contribuables" && (
            <Row>
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={8}>
                        <InputGroup><InputGroup.Text><Search /></InputGroup.Text>
                          <Form.Control type="text" placeholder="Rechercher par nom, téléphone, email..." value={contribuablesFilters.search} onChange={(e) => setContribuablesFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))} />
                        </InputGroup>
                      </Col>
                      <Col md={2}>
                        <Form.Select value={contribuablesFilters.per_page} onChange={(e) => setContribuablesFilters((prev) => ({ ...prev, per_page: Number(e.target.value), page: 1 }))}>
                          <option value="10">10 / page</option>
                          <option value="25">25 / page</option>
                          <option value="50">50 / page</option>
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Button variant="outline-secondary" onClick={() => setContribuablesFilters({ page: 1, per_page: 10, search: "" })} className="w-100">Réinitialiser</Button>
                      </Col>
                    </Row>
                    <div className="table-responsive">
                      <Table hover>
                        <thead className="bg-light"><tr><th>Contribuable</th><th>Contact</th><th className="text-center">Éch.</th><th className="text-end">Total</th><th className="text-end">Payé</th><th className="text-end">Reste</th><th className="text-center">Progression</th></tr></thead>
                        <tbody>
                          {loading ? (
                            <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" /></td></tr>
                          ) : rapportData.contribuables?.data?.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-5"><People size={48} className="text-muted" /><p>Aucun contribuable</p></td></tr>
                          ) : (
                            rapportData.contribuables.data.map((c) => (
                              <tr key={c.id}>
                                <td><div className="fw-medium">{c.nom}</div><small className="text-muted">ID: {c.contribuable_id}</small></td>
                                <td>{c.telephone && <div><Phone size={12} className="me-1" />{c.telephone}</div>}{c.email && <div><Envelope size={12} className="me-1" />{c.email}</div>}</td>
                                <td className="text-center">{c.nombreSouscriptions}</td>
                                <td className="text-end fw-bold">{formatCurrency(c.montantInitialTotal)}</td>
                                <td className="text-end text-success">{formatCurrency(c.montantPayeTotal)}</td>
                                <td className="text-end text-danger">{formatCurrency(c.montantRestant)}</td>
                                <td style={{ width: "120px" }}><ProgressBar now={c.tauxPaiement} style={{ height: "6px" }} /><div className="text-center small mt-1">{Math.round(c.tauxPaiement)}%</div></td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* ONGLET ENRÔLEMENT */}
          {activeTab === "enrollement" && (
            <Row>
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <h6 className="mb-0 fw-bold"><Trophy className="me-2 text-warning" />Statistiques d'enrôlement</h6>
                      <div className="d-flex gap-2">
                        <InputGroup style={{ width: "250px" }}><InputGroup.Text><Search /></InputGroup.Text><Form.Control type="text" placeholder="Rechercher un agent..." value={agentsFilters.search} onChange={(e) => setAgentsFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))} /></InputGroup>
                        <Form.Select value={agentsFilters.per_page} onChange={(e) => setAgentsFilters((prev) => ({ ...prev, per_page: Number(e.target.value), page: 1 }))} style={{ width: "100px" }}>
                          <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option>
                        </Form.Select>
                      </div>
                    </div>
                    <Row className="g-3 mb-4">
                      <Col md={3}><div className="bg-primary bg-opacity-10 rounded p-3 text-center"><h3 className="mb-0 text-primary">{enrollement.global?.total?.count?.toLocaleString() || 0}</h3><small>Total enrôlements</small></div></Col>
                      <Col md={3}><div className="bg-success bg-opacity-10 rounded p-3 text-center"><h3 className="mb-0 text-success">{enrollement.global?.total?.periode || 0}</h3><small>Sur la période</small></div></Col>
                      <Col md={3}><div className="bg-info bg-opacity-10 rounded p-3 text-center"><h3 className="mb-0 text-info">{enrollement.global?.total?.aujourdhui || 0}</h3><small>Aujourd'hui</small></div></Col>
                      <Col md={3}><div className="bg-warning bg-opacity-10 rounded p-3 text-center"><h3 className="mb-0 text-warning">{enrollement.totauxAgents?.nombre_agents || 0}</h3><small>Agents actifs</small></div></Col>
                    </Row>
                    <div className="table-responsive">
                      <Table hover className="align-middle">
                        <thead className="bg-light"><tr><th className="text-center">Rang</th><th style={{ width: "130px" }}>Date création</th><th style={{ width: "100px" }}>ID Agent</th><th>Nom de l'agent</th><th className="text-center">Nbre contribuables</th></tr></thead>
                        <tbody>
                          {loading ? (
                            <tr><td colSpan="5" className="text-center py-5"><Spinner animation="border" /></td></tr>
                          ) : enrollement.parAgent?.length === 0 ? (
                            <tr><td colSpan="5" className="text-center py-5"><PersonPlus size={48} className="text-muted mb-2" /><p className="text-muted">Aucun agent trouvé</p></td></tr>
                          ) : (
                            enrollement.parAgent.map((agent) => {
                              const createDate = agent.created_at ? new Date(agent.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
                              return (
                                <tr key={agent.user_id}>
                                  <td className="text-center fw-bold">{agent.rang === 1 ? <Trophy size={18} className="text-warning" /> : agent.rang}</td>
                                  <td><small className="text-muted"><Calendar size={12} className="me-1" />{createDate}</small></td>
                                  <td><code className="small bg-light px-2 py-1 rounded">{agent.user_id}</code></td>
                                  <td><div className="fw-medium">{agent.nom_complet || "-"}</div>{agent.email && <small className="text-muted d-block">{agent.email}</small>}</td>
                                  <td className="text-center"><Badge bg="primary" className="px-3 py-2 rounded-pill">{agent.total_enroles || 0}</Badge></td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* ONGLET PROCÈS-VERBAUX */}
          {activeTab === "proces-verbaux" && (
            <Row>
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                      <div>
                        <h6 className="mb-0 fw-bold"><FileEarmarkText className="me-2 text-primary" />Génération des procès-verbaux de collecte</h6>
                        <small className="text-muted">Générez un PV récapitulatif par agent pour la période sélectionnée. Les montants sont issus du système.</small>
                      </div>
                      <Button variant="primary" onClick={handleOpenPVModal} disabled={loading || exporting || loadingPV}>
                        {loadingPV ? (<><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Chargement...</>) : (<><PencilSquare className="me-2" />Configurer le PV</>)}
                      </Button>
                    </div>
                    {loading ? (
                      <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : (rapportData.enrollement?.parAgent || []).length === 0 ? (
                      <div className="text-center py-5"><FileEarmarkText size={48} className="text-muted" /><p className="mt-2 text-muted">Aucun agent disponible pour générer un PV.</p></div>
                    ) : (
                      <>
                        <Row className="g-3 mb-4">
                          <Col md={4}><div className="bg-primary bg-opacity-10 rounded p-3 text-center"><h4 className="mb-0 text-primary">{(allAgentsForPV.length > 0 ? allAgentsForPV : rapportData.enrollement.parAgent || []).length}</h4><small>Agents à inclure</small></div></Col>
                          <Col md={4}><div className="bg-success bg-opacity-10 rounded p-3 text-center"><h4 className="mb-0 text-success">{formatCurrency(rapportData.enrollement.totauxAgents?.total_collecte || rapportData.stats_globaux?.totalPaye || 0)}</h4><small>Total collecté</small></div></Col>
                          <Col md={4}><div className="bg-info bg-opacity-10 rounded p-3 text-center"><h4 className="mb-0 text-info">{(rapportData.journalier || []).reduce((s, j) => s + parseInt(j.nombre_collectes || 0, 10), 0)}</h4><small>Opérations</small></div></Col>
                        </Row>
                        <div className="table-responsive">
                          <Table hover size="sm" className="align-middle">
                            <thead className="bg-light"><tr><th className="text-center">#</th><th>Agent</th><th className="text-center">Enrôlements</th><th className="text-end">Montant collecté</th></tr></thead>
                            <tbody>
                              {(rapportData.enrollement.parAgent || []).map((agent, idx) => {
                                const montantCollecte = parseFloat(agent.montant_collecte || 0);
                                const matriculeAffiche = cleanMatricule(agent);
                                return (
                                  <tr key={agent.user_id}>
                                    <td className="text-center">{idx + 1}</td>
                                    <td><div className="fw-medium">{agent.nom_complet || "-"}</div>{matriculeAffiche && <small className="text-muted">Matricule : {matriculeAffiche}</small>}</td>
                                    <td className="text-center"><Badge bg="primary" pill>{agent.total_enroles || 0}</Badge></td>
                                    <td className="text-end fw-bold text-success">{formatCurrency(montantCollecte)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* MODALE : Configuration du procès-verbal */}
          <Modal show={pvModalOpen} onHide={() => setPvModalOpen(false)} size="lg" centered backdrop="static">
            <Modal.Header closeButton><Modal.Title><FileEarmarkText className="me-2" />Configuration du procès-verbal</Modal.Title></Modal.Header>
            <Modal.Body>
              {pvData && (
                <>
                  <div className="alert alert-info py-2 mb-3 small">
                    <strong>Période :</strong> {pvData.periode.debut} → {pvData.periode.fin} &nbsp;|&nbsp;
                    <strong>Agents :</strong> {pvData.agents.length} &nbsp;|&nbsp;
                  </div>
                  <Row className="g-3">
                    <Col md={12}><Form.Label className="small fw-semibold">Service</Form.Label><Form.Control size="sm" type="text" placeholder="(à venir)" value={pvMeta.service} onChange={(e) => setPvMeta((p) => ({ ...p, service: e.target.value }))} /></Col>
                    <Col md={6}>
                      <Form.Label className="small fw-semibold">Lieu de signature <Badge bg="success" pill>auto</Badge></Form.Label>
                      <Form.Control size="sm" type="text" placeholder="Ex: LEMBA" value={pvMeta.lieu} onChange={(e) => setPvMeta((p) => ({ ...p, lieu: e.target.value }))} />
                      <small className="text-muted">Détecté automatiquement depuis le nom de votre commune</small>
                    </Col>
                    <Col md={6}><Form.Label className="small fw-semibold">Date de validation</Form.Label><Form.Control size="sm" type="date" value={pvMeta.dateValidation} onChange={(e) => setPvMeta((p) => ({ ...p, dateValidation: e.target.value }))} /></Col>
                    <Col md={4}><Form.Label className="small fw-semibold">Responsable de la collecte</Form.Label><Form.Control size="sm" type="text" placeholder="(à venir)" value={pvMeta.responsableCollecte} onChange={(e) => setPvMeta((p) => ({ ...p, responsableCollecte: e.target.value }))} /></Col>
                    <Col md={4}><Form.Label className="small fw-semibold">Responsable financier / comptable</Form.Label><Form.Control size="sm" type="text" placeholder="(à venir)" value={pvMeta.responsableFinancier} onChange={(e) => setPvMeta((p) => ({ ...p, responsableFinancier: e.target.value }))} /></Col>
                    <Col md={4}><Form.Label className="small fw-semibold">Responsable de validation</Form.Label><Form.Control size="sm" type="text" placeholder="(à venir)" value={pvMeta.responsableValidation} onChange={(e) => setPvMeta((p) => ({ ...p, responsableValidation: e.target.value }))} /></Col>
                    <Col md={12}><Form.Label className="small fw-semibold">Observations (optionnel)</Form.Label><Form.Control as="textarea" rows={3} size="sm" placeholder="Écarts constatés, justifications, régularisations à prévoir..." value={pvMeta.observations} onChange={(e) => setPvMeta((p) => ({ ...p, observations: e.target.value }))} /></Col>
                  </Row>
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="outline-secondary" size="sm" onClick={handlePreviewPV} disabled={exporting || loadingPV}>
                {loadingPV ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <FileEarmarkText className="me-1" />}
                Aperçu
              </Button>
              <div className="d-flex gap-2">
                <Button variant="outline-danger" size="sm" onClick={handleExportPVPdf} disabled={exporting || loadingPV}>
                  {loadingPV ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <FileEarmarkPdf className="me-1" />}
                  PDF
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setPvModalOpen(false)}>Fermer</Button>
              </div>
            </Modal.Footer>
          </Modal>

          {/* MODAL D'APERÇU DU PV */}
          <Modal show={pvPreviewOpen} onHide={() => setPvPreviewOpen(false)} size="lg" centered backdrop="static" dialogClassName="pv-preview-modal" style={{ maxWidth: "95vw" }}>
            <Modal.Header closeButton className="bg-light"><Modal.Title><FileEarmarkText className="me-2 text-primary" />Aperçu du procès-verbal</Modal.Title></Modal.Header>
            <Modal.Body style={{ background: "#e9ecef", padding: "15px", overflow: "auto", maxHeight: "75vh" }}>
              {!logosReady ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: "300px" }}>
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted mb-0">Chargement des logos…</p>
                </div>
              ) : (
                <>
                  <div style={{ background: "#ffffff", width: "210mm", maxWidth: "100%", margin: "0 auto", padding: "15mm", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", boxSizing: "border-box" }}>
                    <div
                      id="pv-preview-content"
                      dangerouslySetInnerHTML={{ __html: pvData ? generatePVHtml(pvData, pvMeta, pvLogos) : "" }}
                    />
                  </div>
                  <div className="alert alert-warning py-2 px-3 small mt-3" style={{ maxWidth: "210mm", margin: "0 auto" }}>
                    <strong>💡 Astuce :</strong> Dans la boîte d'impression, laissez <strong>« Marges : Par défaut »</strong> et
                    décochez <strong>« En-têtes et pieds de page »</strong> pour que seule la numérotation du PV (« Page X / Y ») s'affiche en bas de chaque page.
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between bg-light">
              <Button variant="outline-secondary" onClick={() => setPvPreviewOpen(false)}>Fermer</Button>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={handlePrintPVFromModal} disabled={!logosReady}>
                  <FileEarmarkPdf className="me-1" />Imprimer / Enregistrer en PDF
                </Button>
              </div>
            </Modal.Footer>
          </Modal>

          <style>{`
            .stats-card { transition: all 0.3s ease; border-radius: 16px !important; overflow: hidden; min-height: 160px; }
            .stats-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important; }
            .bg-success-subtle { background-color: rgba(25, 135, 84, 0.1); }
            .bg-info-subtle { background-color: rgba(13, 202, 240, 0.1); }
            .bg-danger-subtle { background-color: rgba(220, 53, 69, 0.1); }
            .evolution-chart { position: relative; }
            .evolution-tooltip { display: none; position: absolute; top: -30px; left: 50%; transform: translateX(-50%); background: #333; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; white-space: nowrap; }
            .bg-primary:hover .evolution-tooltip { display: block; }
            .progress-circle { position: relative; border-radius: 50%; }
            .custom-tabs .nav-link { border: none; color: #6c757d; padding: 0.75rem 1.5rem; font-weight: 500; }
            .custom-tabs .nav-link:hover { color: #506fd9; }
            .custom-tabs .nav-link.active { color: #506fd9; font-weight: 600; border-bottom: 3px solid #506fd9; background: none; }
            @media (max-width: 768px) {
              .evolution-chart .d-flex { flex-direction: column; align-items: center; gap: 20px; }
              .evolution-chart .text-center { width: 100% !important; display: flex; align-items: center; gap: 15px; }
              .evolution-chart .bg-primary { height: 40px !important; width: 100% !important; border-radius: 4px; }
            }
            .pv-preview-modal { max-width: 850px !important; margin: 1.75rem auto; }
            .pv-preview-modal .modal-content { border-radius: 8px; overflow: hidden; max-height: 90vh; }
            .pv-preview-modal .modal-body { overflow-y: auto; }

            #pv-preview-content { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; line-height: 1.3; }
            #pv-preview-content .main-title-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            #pv-preview-content .main-title-table td { border: 1px solid #000; padding: 8px; text-align: center; font-weight: bold; text-transform: uppercase; }
            #pv-preview-content .agent-table { width: 100%; border-collapse: collapse; font-size: 10pt; }
            #pv-preview-content .agent-table th { border: 1px solid #000; padding: 8px; text-align: left; font-weight: bold; background: #fff; }
            #pv-preview-content .agent-table td { border: 1px solid #000; padding: 8px; }
            #pv-preview-content .signature-box { border: 1px solid #000; border-top: none; padding: 12px; min-height: 50px; }
            #pv-preview-content .text-right { text-align: right; }
            #pv-preview-content .text-center { text-align: center; }
          `}</style>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
}